<template>
  <div class="demo-user-selector">
    <div class="selector-header">
      <h3>Demo User Selection</h3>
      <p class="selector-description">
        Select a demo user to test with different permission levels
      </p>
    </div>
    
    <div v-if="loading" class="loading">
      Loading demo users...
    </div>
    
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    
    <div v-else class="user-options">
      <div 
        v-for="user in demoUsers" 
        :key="user.id"
        class="user-card"
        :class="{ selected: selectedUserId === user.id }"
        @click="selectUser(user.id)"
      >
        <div class="user-info">
          <h4>{{ user.name }}</h4>
          <p class="username">@{{ user.username }}</p>
          <p class="email">{{ user.email }}</p>
        </div>
        <div class="user-groups">
          <span class="group-label">Groups:</span>
          <span 
            v-for="group in user.groups" 
            :key="group"
            class="group-badge"
            :class="getGroupClass(group)"
          >
            {{ group }}
          </span>
        </div>
        <div v-if="selectedUserId === user.id" class="selected-indicator">
          ✓ Selected
        </div>
      </div>
      
      <div 
        class="user-card no-user"
        :class="{ selected: selectedUserId === null }"
        @click="selectUser(null)"
      >
        <div class="user-info">
          <h4>No User</h4>
          <p class="username">Clear selection</p>
          <p class="email">No permissions</p>
        </div>
        <div v-if="selectedUserId === null" class="selected-indicator">
          ✓ Selected
        </div>
      </div>
    </div>
    
    <div class="selector-actions">
      <button 
        @click="confirmSelection" 
        :disabled="loading || selectedUserId === initialUserId"
        class="confirm-btn"
      >
        Apply Selection
      </button>
      <button 
        @click="$emit('close')"
        class="cancel-btn"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';

interface DemoUser {
  id: string;
  username: string;
  name: string;
  email: string;
  groups: string[];
  attributes?: Record<string, any>;
}

const emit = defineEmits<{
  close: [];
  userSelected: [user: DemoUser | null];
}>();

const demoUsers = ref<DemoUser[]>([]);
const selectedUserId = ref<string | null>(null);
const initialUserId = ref<string | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

const fetchDemoUsers = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const response = await fetch(`${baseUrl}/api/demo-auth/users`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch demo users');
    }
    
    const data = await response.json();
    demoUsers.value = data.users;
    selectedUserId.value = data.selectedUserId;
    initialUserId.value = data.selectedUserId;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load demo users';
    console.error('Error fetching demo users:', err);
  } finally {
    loading.value = false;
  }
};

const selectUser = (userId: string | null) => {
  selectedUserId.value = userId;
};

const confirmSelection = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const response = await fetch(`${baseUrl}/api/demo-auth/select`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId: selectedUserId.value })
    });
    
    if (!response.ok) {
      throw new Error('Failed to select demo user');
    }
    
    const data = await response.json();
    
    // Emit the selected user
    emit('userSelected', data.user);
    
    // Reload the page to apply the new user context
    window.location.reload();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to select user';
    console.error('Error selecting demo user:', err);
  } finally {
    loading.value = false;
  }
};

const getGroupClass = (group: string): string => {
  switch (group) {
    case 'Bridge_Consultant':
      return 'group-admin';
    case 'Admin':
      return 'group-admin';
    case 'Team_Lead':
      return 'group-lead';
    case 'Client':
      return 'group-client';
    default:
      return 'group-default';
  }
};

onMounted(() => {
  fetchDemoUsers();
});
</script>

<style scoped>
.demo-user-selector {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.selector-header {
  margin-bottom: 24px;
}

.selector-header h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #133164; /* Primary Deep Indigo */
}

.selector-description {
  margin: 0;
  color: #5E6977; /* Steel */
  font-size: 14px;
}

.loading,
.error {
  padding: 20px;
  text-align: center;
  font-size: 14px;
}

.error {
  color: #D7263D; /* Error */
  background: rgba(215, 38, 61, 0.1);
  border-radius: 4px;
}

.user-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.user-card {
  border: 2px solid #DBE2EF; /* Slate */
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.user-card:hover {
  border-color: #133164; /* Primary Deep Indigo */
  box-shadow: 0 2px 8px rgba(19, 49, 100, 0.15);
}

.user-card.selected {
  border-color: #133164; /* Primary Deep Indigo */
  background: rgba(19, 49, 100, 0.05);
}

.user-card.no-user {
  background: #DBE2EF; /* Slate */
}

.user-info {
  margin-bottom: 12px;
}

.user-info h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #133164; /* Primary Deep Indigo */
}

.username {
  margin: 0;
  font-size: 13px;
  color: #5E6977; /* Steel */
  font-family: monospace;
}

.email {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #4A5460; /* Charcoal */
}

.user-groups {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.group-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.group-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

.group-admin {
  background: #e8f5e9;
  color: #2e7d32;
}

.group-lead {
  background: #fff3e0;
  color: #e65100;
}

.group-client {
  background: #f3e5f5;
  color: #6a1b9a;
}

.group-default {
  background: #f5f5f5;
  color: #616161;
}

.selected-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  color: #1976d2;
  font-weight: 600;
  font-size: 14px;
}

.selector-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.confirm-btn,
.cancel-btn {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.confirm-btn {
  background: #133164; /* Primary Deep Indigo */
  color: white;
  border: none;
}

.confirm-btn:hover:not(:disabled) {
  background: #0f254a; /* Darker shade of primary */
}

.confirm-btn:disabled {
  background: #DBE2EF; /* Slate */
  color: #5E6977; /* Steel */
  cursor: not-allowed;
}

.cancel-btn {
  background: white;
  color: #5E6977; /* Steel */
  border: 1px solid #DBE2EF; /* Slate */
}

.cancel-btn:hover {
  background: #DBE2EF; /* Slate */
}
</style>